import { supabase } from '../lib/supabase';

export const learningCenterService = {
  // Get all learning modules with user progress
  async getAllModulesWithProgress(userId) {
    const { data, error } = await supabase?.from('learning_modules')?.select(`
        *,
        user_learning_progress!left(
          status,
          progress_percentage,
          completed_at,
          time_spent_minutes
        )
      `)?.eq('user_learning_progress.user_id', userId)?.order('chapter_number');

    if (error) throw error;

    return data?.map(module => ({
      id: module.id,
      title: module.title,
      description: module.description,
      content: module.content,
      chapterNumber: module.chapter_number,
      difficulty: module.difficulty,
      estimatedDuration: module.estimated_duration_minutes,
      learningObjectives: module.learning_objectives,
      isPremium: module.is_premium,
      createdAt: module.created_at,
      progress: module.user_learning_progress?.[0] ? {
        status: module.user_learning_progress?.[0]?.status,
        progressPercentage: module.user_learning_progress?.[0]?.progress_percentage,
        completedAt: module.user_learning_progress?.[0]?.completed_at,
        timeSpent: module.user_learning_progress?.[0]?.time_spent_minutes
      } : null
    }));
  },

  // Get single module with detailed content
  async getModuleById(moduleId) {
    const { data, error } = await supabase?.from('learning_modules')?.select('*')?.eq('id', moduleId)?.single();

    if (error) throw error;

    return {
      id: data?.id,
      title: data?.title,
      description: data?.description,
      content: data?.content,
      chapterNumber: data?.chapter_number,
      difficulty: data?.difficulty,
      estimatedDuration: data?.estimated_duration_minutes,
      prerequisiteModules: data?.prerequisite_modules,
      learningObjectives: data?.learning_objectives,
      isPremium: data?.is_premium,
      createdAt: data?.created_at
    };
  },

  // Update or create user progress
  async updateUserProgress(userId, moduleId, progressData) {
    const { data: { user } } = await supabase?.auth?.getUser();
    if (!user) throw new Error('Not authenticated');

    const { data, error } = await supabase?.from('user_learning_progress')?.upsert({
        user_id: user?.id,
        module_id: moduleId,
        status: progressData?.status,
        progress_percentage: progressData?.progressPercentage,
        time_spent_minutes: progressData?.timeSpent,
        last_accessed_at: new Date()?.toISOString(),
        completed_at: progressData?.status === 'completed' ? new Date()?.toISOString() : null,
        updated_at: new Date()?.toISOString()
      })?.select()?.single();

    if (error) throw error;

    return {
      id: data?.id,
      status: data?.status,
      progressPercentage: data?.progress_percentage,
      timeSpent: data?.time_spent_minutes,
      completedAt: data?.completed_at
    };
  },

  // Get quiz for a module
  async getModuleQuiz(moduleId) {
    const { data: quizData, error: quizError } = await supabase?.from('module_quizzes')?.select(`
        *,
        quiz_questions(*)
      `)?.eq('module_id', moduleId)?.single();

    if (quizError) throw quizError;

    return {
      id: quizData?.id,
      moduleId: quizData?.module_id,
      title: quizData?.title,
      description: quizData?.description,
      passingScore: quizData?.passing_score,
      timeLimit: quizData?.time_limit_minutes,
      maxAttempts: quizData?.max_attempts,
      questions: quizData?.quiz_questions?.map(q => ({
        id: q?.id,
        questionText: q?.question_text,
        questionType: q?.question_type,
        options: q?.options,
        correctAnswer: q?.correct_answer,
        explanation: q?.explanation,
        points: q?.points,
        orderNumber: q?.order_number
      }))
    };
  },

  // Submit quiz attempt
  async submitQuizAttempt(quizId, answers) {
    const { data: { user } } = await supabase?.auth?.getUser();
    if (!user) throw new Error('Not authenticated');

    // Get previous attempts count
    const { data: attempts } = await supabase?.from('user_quiz_attempts')?.select('attempt_number')?.eq('user_id', user?.id)?.eq('quiz_id', quizId)?.order('attempt_number', { ascending: false })?.limit(1);

    const attemptNumber = attempts?.length ? attempts?.[0]?.attempt_number + 1 : 1;

    const { data, error } = await supabase?.from('user_quiz_attempts')?.insert({
        user_id: user?.id,
        quiz_id: quizId,
        score: answers?.score,
        passed: answers?.passed,
        answers: answers?.userAnswers,
        attempt_number: attemptNumber,
        completed_at: new Date()?.toISOString(),
        time_taken_minutes: answers?.timeTaken
      })?.select()?.single();

    if (error) throw error;

    return {
      id: data?.id,
      score: data?.score,
      passed: data?.passed,
      attemptNumber: data?.attempt_number,
      timeTaken: data?.time_taken_minutes
    };
  },

  // Get user learning statistics
  async getUserStats(userId) {
    const { data, error } = await supabase?.rpc('get_user_learning_stats', { p_user_id: userId });

    if (error) throw error;

    return {
      modulesCompleted: data?.[0]?.modules_completed || 0,
      modulesInProgress: data?.[0]?.modules_in_progress || 0,
      certificatesEarned: data?.[0]?.certificates_earned || 0,
      totalTimeSpent: data?.[0]?.total_time_spent || 0,
      averageQuizScore: parseFloat(data?.[0]?.average_quiz_score || 0)
    };
  },

  // Get user bookmarks
  async getUserBookmarks(userId) {
    const { data, error } = await supabase?.from('user_bookmarks')?.select(`
        *,
        learning_modules(id, title, description, chapter_number)
      `)?.eq('user_id', userId)?.order('created_at', { ascending: false });

    if (error) throw error;

    return data?.map(bookmark => ({
      id: bookmark?.id,
      createdAt: bookmark?.created_at,
      module: {
        id: bookmark?.learning_modules?.id,
        title: bookmark?.learning_modules?.title,
        description: bookmark?.learning_modules?.description,
        chapterNumber: bookmark?.learning_modules?.chapter_number
      }
    }));
  },

  // Add/remove bookmark
  async toggleBookmark(moduleId) {
    const { data: { user } } = await supabase?.auth?.getUser();
    if (!user) throw new Error('Not authenticated');

    // Check if bookmark exists
    const { data: existing } = await supabase?.from('user_bookmarks')?.select('id')?.eq('user_id', user?.id)?.eq('module_id', moduleId)?.single();

    if (existing) {
      // Remove bookmark
      const { error } = await supabase?.from('user_bookmarks')?.delete()?.eq('id', existing?.id);

      if (error) throw error;
      return { bookmarked: false };
    } else {
      // Add bookmark
      const { error } = await supabase?.from('user_bookmarks')?.insert({
          user_id: user?.id,
          module_id: moduleId
        });

      if (error) throw error;
      return { bookmarked: true };
    }
  },

  // Search modules
  async searchModules(query, filters = {}) {
    let queryBuilder = supabase?.from('learning_modules')?.select('*')?.ilike('title', `%${query}%`);

    if (filters?.difficulty) {
      queryBuilder = queryBuilder?.eq('difficulty', filters?.difficulty);
    }

    if (filters?.isPremium !== undefined) {
      queryBuilder = queryBuilder?.eq('is_premium', filters?.isPremium);
    }

    queryBuilder = queryBuilder?.order('chapter_number');

    const { data, error } = await queryBuilder;

    if (error) throw error;

    return data?.map(module => ({
      id: module.id,
      title: module.title,
      description: module.description,
      chapterNumber: module.chapter_number,
      difficulty: module.difficulty,
      estimatedDuration: module.estimated_duration_minutes,
      isPremium: module.is_premium
    }));
  }
};